<template>
  <div class="collections__item">
    <router-link :to="view.startLinkParams(doc)" class="collections__link">
      <div
        class="collections__img-box"
        :class="
          coverImages
            ? 'collections__img-box--dynamic'
            : 'collections__img-box--static'
        "
      >
        <template v-if="coverImages">
          <DynamicCover
            v-for="n in 3"
            :key="n"
            :position="positionMap[n - 1]"
            :image="coverImages[n - 1]"
            class="collections__dynamic-img"
          />
        </template>
        <img
          v-else
          v-lazy="{ src: doc.cover_image }"
          crossorigin="anonymous"
          class="collections__img"
          alt=""
        />
      </div>
      <div class="collections__text-box">
        <h3 class="collections__name card__title">{{ doc.title }}</h3>
        <h4 class="collections__count">
          {{ doc.doc_count }}
          {{ doc.doc_count == 1 ? 'book' : 'books' }}
        </h4>
        <slot></slot>
      </div>
    </router-link>
  </div>
</template>

<script setup lang="ts">
import type { CollectionListItem, CustomList } from '@/models/interfaces'
import { CollectionsView } from '@/models/docs.list.collection'
import type { CustomListsView } from '@/models/custom.lists'
import DynamicCover from './DynamicCover.vue'

defineProps<{
  doc: CollectionListItem | CustomList
  view: CollectionsView | CustomListsView
  coverImages?: string[]
}>()

const positionMap = ['center', 'left', 'right'] as const
</script>

<style lang="scss" scoped>
.collections {
  &__item {
    position: relative;
  }

  &__link {
    background-color: $c-black-17;
    height: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    border: 1px solid $c-border;

    @include hover {
      border-color: $c-border-2;
      color: initial;
      box-shadow: 0px 10px 10px rgb(20 28 49 / 3%);

      .collections__img,
      .collections__dynamic-img {
        transform: scale(1.07);
      }
    }
  }

  &__text-box {
    position: relative;
    padding: 16px 19px 19px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: all 0.2s ease-in-out;
    background-color: $c-white;
    border-radius: 0 0 12px 12px;
  }

  &__img-box {
    display: flex;
    justify-content: center;
    align-items: center;
    background: $c-black-17;
    border-radius: 12px 12px 0 0;
    padding: 26px 19px 24px;
    text-align: center;
    overflow: hidden;
  }

  &__img-box--static {
    height: 189px;

    @media only screen and (max-width: $w-medium-s) {
      height: 170px;
    }
  }

  &__img {
    margin: 0 auto;
    filter: $shadow-f;
    border-radius: 10px;
    width: 196px;
    transition: all 0.2s ease-in-out;

    @media only screen and (max-width: $w-medium-s) {
      width: 170px;
    }
  }

  &__dynamic-img {
    transition: all 0.2s ease-in-out;
  }

  &__name {
    font-size: 18px;
    line-height: 24px;
    color: $c-black-2;

    @media only screen and (max-width: $w-tablet-s) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  &__count {
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: #999ba4;
    margin-top: 8px;

    @media only screen and (max-width: $w-tablet-s) {
      font-size: 12px;
      line-height: 14px;
    }
  }
}

.night-mode {
  .collections {
    &__text-box {
      background-color: $c-black-7;
    }

    &__name {
      color: $c-white;
    }

    &__link {
      border-color: $c-dark-3;
      background-color: $c-black-12;
      @include hover {
        border-color: $c-dark-8 !important;
      }
    }

    &__img-box {
      background-color: $c-black-12;
    }
  }
}
</style>
